import { ConfirmDialog } from '@aibees/shared/components';
import { useBoolean } from '@aibees/shared/hooks';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import { DialogPresetId, dialogPresets } from './dialog-presets';
import * as SX from './sx';

interface OpenDialogArgs {
  preset: DialogPresetId;
}

interface DialogManagerContextValue {
  openDialog: (args: OpenDialogArgs) => void;
}

const DialogManagerContext = createContext<DialogManagerContextValue | null>(
  null
);

export const useDialogManager = () => {
  const ctx = useContext(DialogManagerContext);
  if (!ctx) throw new Error('DialogManagerProvider is missing');
  return ctx;
};

export const DialogManagerProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const state = useBoolean();
  const [dialogConfig, setDialogConfig] = useState<DialogPresetId | null>(null);

  const openDialog = useCallback(
    (args: OpenDialogArgs) => {
      setDialogConfig(args.preset);
      state.onTrue();
    },
    [state]
  );

  const closeDialog = () => {
    state.onFalse();
    setDialogConfig(null);
  };

  const preset = dialogConfig ? dialogPresets[dialogConfig] : null;

  return (
    <DialogManagerContext.Provider value={{ openDialog }}>
      {children}

      {preset && (
        <ConfirmDialog
          state={state}
          title={preset.title}
          okTitle={preset.okTitle}
          okProps={preset.okProps}
          cancelTitle="Cancel"
          onCancel={closeDialog}
          onOk={async () => {
            await preset.action?.();
            closeDialog();
          }}
          cancelProps={{ variant: 'text', color: 'inherit' }}
          content={preset.content}
          actionsSx={SX.actions}
        />
      )}
    </DialogManagerContext.Provider>
  );
};
